"use client";

import { PortableText } from "next-sanity";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";
import {
  PageAnniversarySchema,
  PageAnniversaryFeatureItem,
} from "@/sanity/pages/PageAnniversaryTemplate";
import Image from "@/helpers/Image";
import { cn } from "@/lib/utils";

export default function FeaturesSection({
  data,
  className,
}: {
  data: PageAnniversarySchema;
  className?: string;
}) {
  if (!data.featuresTitle && !data.featureItems?.length) return null;

  return (
    <section
      className={cn("w-full bg-white pb-[5rem] lg:pb-[20rem]", className)}
    >
      <div className="mx-auto w-[min(160.5rem,100%)] px-[3rem] lg:px-0 flex flex-col lg:flex-row gap-[4rem] lg:gap-[11.625rem]">
        {/* Left: sticky on desktop */}
        <div className="lg:sticky lg:top-[15rem] lg:self-start lg:flex-shrink-0 lg:w-[70rem] flex flex-col gap-[5rem] justify-start">
          <div className="flex flex-col gap-[2.25rem]">
            {data.featuresChip && (
              <p className="mb-0 font-blacker italic text-[1.875rem] leading-[1.5] text-black">
                {data.featuresChip}
              </p>
            )}
            {data.featuresTitle && (
              <div className="richtext richtext-start prose-headings:font-medium max-sm:prose-headings:text-[5rem] lg:prose-headings:text-[8rem] max-sm:prose-headings:tracking-[-0.25rem]">
                <PortableText
                  value={data.featuresTitle}
                  components={richTextComponent}
                />
              </div>
            )}
          </div>

          {data.featuresCTA && (
            <div className="w-full lg:w-fit">
              <CTAButton data={data.featuresCTA} />
            </div>
          )}
        </div>

        {/* Right: timeline + cards */}
        <div className="flex-1 relative overflow-y-clip">
          {/* Timeline track — desktop only */}
          <div className="absolute top-0 left-[-1.25rem] lg:left-0 h-full w-[2.5rem]">
            <div className="relative w-full h-full pt-[1rem]">
              <div className="absolute top-0 left-1/2 h-full -translate-x-1/2 w-[2px] bg-[#228e71]/30" />
              <div className="sticky top-[50%] after:h-[100vh] after:w-[2px] after:bg-[#228e71] after:absolute after:left-1/2 after:-translate-x-1/2 after:bottom-0" />
              <div className="sticky bg-[rgba(202,223,217,0.37)] backdrop-blur-[0.5rem] w-[2.75rem] h-[2.75rem] top-[50%] -translate-x-[0.2rem] flex items-center justify-center">
                <div className="bg-[#228e71] w-[1.22rem] h-[1.22rem]" />
              </div>
            </div>
          </div>

          {/* Cards */}
          {!!data.featureItems?.length && (
            <ul className="flex flex-col gap-[2.25rem] pl-[3rem] lg:pl-[11rem] pr-[1rem] lg:pr-0">
              {data.featureItems.map((item: PageAnniversaryFeatureItem, i) => (
                <li
                  key={item._key ?? i}
                  className="bg-white border border-black/10 overflow-hidden"
                >
                  <div className="flex flex-col gap-[5rem] p-[3rem]">
                    {/* Icon */}
                    {item.icon && (
                      <Image
                        src={item.icon}
                        alt=""
                        className="!w-[7rem] !h-[7rem] object-contain flex-shrink-0"
                      />
                    )}

                    {/* Text */}
                    <div className="flex flex-col gap-[1rem]">
                      {item.title && (
                        <p className="mb-0 font-geist font-medium text-[2.75rem] lg:text-[3rem] leading-[1.3] text-[#001b14]">
                          {item.title}
                        </p>
                      )}
                      {item.body && (
                        <p className="mb-0 font-geist text-[2.25rem] leading-[1.5] text-[#001b14]/80">
                          {item.body}
                        </p>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {data.featuresCTA && (
          <div className="lg:hidden w-full lg:w-fit">
            <CTAButton data={data.featuresCTA} />
          </div>
        )}
      </div>
    </section>
  );
}
